/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */

/**
 * 
 * @calculateDistance
 * 
 * Calculates the distance traveled between two consecutive events recursively,
 * taking into account missing odometer readings at the start or end of the event list.
 * @param events: number[] - An array of gps odometer readings (numbers).
 * @returns The total distance traveled between the specified events, in the same units as the odometer readings.
 */
export const calculateDistance = (
  events: (string | number)[],
  startIndex: number = 0,
  endIndex: number = events?.length - 1
): number => {
  // Base case: if there are no events or only one event, return 0 distance
  if (startIndex >= endIndex) {
    return 0;
  }
  let firstOdometer = events[startIndex];
  let lastOdometer = events[endIndex];

  // If either the first or last event's odometer reading is null,
  // skip over any events with null odometer readings in between
  if (!firstOdometer || !lastOdometer) {
    let nextIndex = startIndex + 1;
    let lastIndex = endIndex - 1;
    while (nextIndex <= lastIndex) {
      if (!firstOdometer) {
        firstOdometer = events[nextIndex];
        nextIndex++;
      }
      if (!lastOdometer) {
        lastOdometer = events[lastIndex];
        lastIndex--;
      }
      if (firstOdometer && lastOdometer) {
        break;
      }
    }
    // No events with non-null odometer readings were found, so return null
    if (!firstOdometer || !lastOdometer) {
      return null;
    }
  }

  // Calculate the distance between the first and last odometer readings
  const distance: number = Math.round(
    (parseFloat(lastOdometer.toString()) - parseFloat(firstOdometer.toString())) * 10
  ) / 10;

  // Return the absolute value of the distance
  return Math.abs(distance);
};
